import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Image,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Grid,
  GridItem,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
} from "@chakra-ui/react";
import { Helmet } from 'react-helmet-async';
import { ChevronRightIcon } from "@chakra-ui/icons";
import { FaLinkedin, FaYoutube } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import FeatureCard from "../../components/ProductDetails/ProductFeatureCard";
import CommonFeatureCard from "../../components/Common/FeatureCard";
import ServiceCard from "../../components/Common/ServiceCard";

const InsightDBPage = () => {
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Demo URL - you can change this to your actual demo URL
  const demoUrl = "https://demo.insightdb.ai"; // Replace with actual demo URL

  // Core Modules & Capabilities Data
  const coreModules = [
    {
      icon: "/svg/product_gate.svg",
      title: "Data Integration",
      description:
        "Connect to multiple sources (SQL, NoSQL, Cloud, Sheets), with no programming expertise needed (insightdb.ai)",
    },
    {
      icon: "/svg/product_grid.svg",
      title: "Dashboard Builder",
      description:
        "Drag-and-drop interface for interactive, personalized dashboards",
    },
    {
      icon: "/svg/product_graph.svg",
      title: "Real-Time Reporting",
      description: "Instant data updates and automated, on-demand reports.",
    },
    {
      icon: "/svg/product_api.svg",
      title: "API & Notification Hub",
      description:
        "Create secure data APIs; configure real-time alerts via SMS, email etc.",
    },
    {
      icon: "/svg/product_hand_setting.svg",
      title: "Security & Access",
      description:
        "Keycloak-managed authentication, role-based permissions, strong encryption",
    },
    {
      icon: "/svg/product_files.svg",
      title: "Scalability",
      description:
        "Kubernetes-run cloud microservice architecture ensures resilience",
    },
  ];

  // Use Cases Data (for CommonFeatureCard)
  const useCases = [
    {
      id: 1,
      title: "Government Dashboards",
      description:
        "Monitor citizen service usage, licensing processing, and digital adoption in real time.",
    },
    {
      id: 2,
      title: "Donor-Funded Project Monitoring",
      description:
        "Track KPIs, budgets, and outcomes for ADB, World Bank, UNDP, EU, USAID programs.",
    },
    {
      id: 3,
      title: "Enterprise Operations",
      description:
        "Centralize performance metrics—finance, procurement, HR, customer service—across departments.",
    },
    {
      id: 4,
      title: "Smart City Analytics",
      description:
        "Real-time data collection and notification systems for IoT-based public service infrastructure.",
    },
    {
      id: 5,
      title: "Mobile-First Insights",
      description:
        "Field staff get live updates and alerts through accessible mobile dashboards.",
    },
  ];

  // Why Choose InsightDB Features Data (for ServiceCard)
  const whyChooseFeatures = [
    {
      id: 1,
      title: "Tailored Deployment",
      description:
        "Options include on-premise or cloud, with customization for public sector and donor compliance.",
      icon: "/svg/frame_1.svg",
      bgColor: "#E3F2FD",
      iconColor: "#000B8C",
    },
    {
      id: 2,
      title: "Fast Implementation",
      description:
        "Get working analytics dashboards within a day—zero coding required.",
      icon: "/svg/frame_2.svg",
      bgColor: "#E8F5E8",
      iconColor: "#2E7D32",
    },
    {
      id: 3,
      title: "Support & Maintenance",
      description:
        "24/7 SLAs, release updates, and training to build in-house data capabilities.",
      icon: "/svg/frame_3.svg",
      bgColor: "#FFF3E0",
      iconColor: "#F57C00",
    },
    {
      id: 4,
      title: "Trusted Delivery",
      description:
        "Powering analytics for large institutions, donor-funded tech projects, and public-sector systems.",
      icon: "/svg/frame_4.svg",
      bgColor: "#FCE4EC",
      iconColor: "#C2185B",
    },
    {
      id: 5,
      title: "Scalable & Secure",
      description:
        "Secure throughout, hosted on Kubernetes, compliant with international data norms.",
      icon: "/svg/frame_5.svg",
      bgColor: "#F3E5F5",
      iconColor: "#7B1FA2",
    },
  ];

  return (
    <>
      <Helmet>
        {/* Basic Meta Tags */}
        <title>InsightDB | No-Code Real-Time Analytics Platform</title>
        <meta 
          name="description" 
          content="InsightDB is a secure, no-code analytics system that brings real-time insights to government and enterprise in Bangladesh—AI-ready, cloud-scalable, and secure." 
        />
        <meta 
          name="keywords" 
          content="Real-time analytics platform Bangladesh, No-code data integration tool, Government dashboard software, Public sector analytics Bangladesh, Cloud-based BI tool, InsightDB platform, Kubernetes analytics platform, Data security Bangladeshi ITES, Donor project analytics Bangladesh, BI tool in Bangladesh" 
        />
        <link rel="canonical" href="https://www.ba-systems.com/products/insightdb" />
        <meta name="robots" content="index, follow" />

        {/* Open Graph / Facebook */}
        <meta property="og:url" content="https://ba-systems.com/products/insightdb" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="InsightDB | No-Code Real-Time Analytics Platform" />
        <meta property="og:description" content="InsightDB is a secure, no-code analytics system that brings real-time insights to government and enterprise in Bangladesh—AI-ready, cloud-scalable, and secure." />
        <meta property="og:image" content="https://opengraph.b-cdn.net/production/images/e1930f8a-3aa2-4fe1-b6ea-0ad5030ebc97.png?token=vTkCXbq7qBnee_uxOFTj3-OKo7vP2KVzX66L6BYKzho&height=596&width=1200&expires=33297038875" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta property="twitter:domain" content="ba-systems.com" />
        <meta property="twitter:url" content="https://ba-systems.com/products/insightdb" />
        <meta name="twitter:title" content="InsightDB | No-Code Real-Time Analytics Platform" />
        <meta name="twitter:description" content="InsightDB is a secure, no-code analytics system that brings real-time insights to government and enterprise in Bangladesh—AI-ready, cloud-scalable, and secure." />
        <meta name="twitter:image" content="https://opengraph.b-cdn.net/production/images/e1930f8a-3aa2-4fe1-b6ea-0ad5030ebc97.png?token=vTkCXbq7qBnee_uxOFTj3-OKo7vP2KVzX66L6BYKzho&height=596&width=1200&expires=33297038875" />
      </Helmet>
      
      <Box
        py={6}
        backgroundImage={"/png/background.png"}
        backgroundSize="cover"
        backgroundPosition="center"
        backgroundRepeat="no-repeat"
        minH="100vh"
        position="relative"
      >
      <Box position="relative" zIndex={1}>
        {/* Breadcrumb Section */}
        <Box>
          <Container maxW="8xl" px={{ base: 4, md: 6 }}>
            <Breadcrumb
              spacing="8px"
              separator={<ChevronRightIcon color="gray.500" />}
              fontSize="sm"
            >
              <BreadcrumbItem>
                <BreadcrumbLink
                  color="#000B8C"
                  _hover={{ textDecoration: "underline" }}
                  onClick={() => navigate("/")}
                  cursor="pointer"
                >
                  Home
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbItem isCurrentPage>
                <Text color="gray.600">InsightDB</Text>
              </BreadcrumbItem>
            </Breadcrumb>
          </Container>
        </Box>

        <Container maxW="7xl" py={{ base: 4, md: 6 }}>
          <HStack align="center" wrap="wrap">
            <VStack
              align="start"
              spacing={0}
              flex={{ base: 1, lg: 3 }}
              minW={{ base: "100%", md: "500px", lg: "700px" }}
            >
              <Image
                src="/png/insightdb.png"
                alt="InsightDB Logo"
                height={{ base: "40px", md: "56px" }}
                objectFit="contain"
                loading="lazy"
                fallbackSrc="/png/ossp_logo.png"
              />

              <Heading
                as="h1"
                fontSize={{ base: "2xl", md: "4xl" }}
                fontWeight="bold"
                mt={4}
                color="#000B8C"
                lineHeight="1.2"
              >
                InsightDB: Real-Time, No-Code{" "}
                <Text as="span" color="gray.700">
                  Data Analytics Platform
                </Text>
              </Heading>

              <Text
                fontSize={{ base: "lg", md: "xl" }}
                color="gray.600"
                fontWeight="medium"
                pt={0}
                fontStyle={"italic"}
              >
                Empower Decisions with Actionable Insights
              </Text>

              <Text
                fontSize={{ base: "md", md: "lg" }}
                color="gray.700"
                lineHeight="1.2"
                mt={4}
                textAlign="justify"
                maxW={{ base: "100%", md: "700px", lg: "800px" }}
              >
                InsightDB is Business Automation's cloud-enabled, no-code
                analytics system designed to help organizations harness data for
                strategic business intelligence. Built to support e-commerce,
                public sector programs, and large-scale digital projects,
                InsightDB brings clarity, agility, and insight to every kind of
                decision-making.
              </Text>
            </VStack>

            <Box
              flex={{ base: 1, lg: 1 }}
              minW={{ base: "100%", md: "350px" }}
              maxW={{ base: "100%", md: "300px" }}
              display="flex"
              alignItems="center"
              justifyContent="center"
              h="100%"
            >
              <Image
                src="/png/obj.png"
                alt="InsightDB Platform Illustration"
                maxW="100%"
                height="auto"
                borderRadius="lg"
                loading="lazy"
                fallbackSrc="/png/default-product.png"
              />
            </Box>
          </HStack>
        </Container>

        {/* Usage of InsightDB Section */}
        <Box py={{ base: 12, md: 16 }} position="relative" overflow="hidden">
          <Container maxW="7xl">
            <VStack spacing={6} align="center" textAlign="center">
              {/* Section Title */}

              <Heading
                as="h1"
                fontSize={{ base: "2xl", md: "4xl" }}
                fontWeight="bold"
                color="#000B8C"
                lineHeight="1.2"
              >
                <Text as="span" color="gray.700">
                  Usage of{" "}
                </Text>
                InsightDB
              </Heading>
            </VStack>
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              mt={8}
              w="100%"
            >
              <Image
                py={8}
                src="/png/insightInfo.png"
                alt="InsightDB Usage Diagram"
                maxW={{ base: "90%", md: "1000px" }}
                w="100%"
                borderRadius="lg"
                loading="lazy"
                fallbackSrc="/png/default-product.png"
              />
            </Box>
          </Container>
        </Box>

        {/* Core Modules & Capabilities Section */}
        <Container maxW="7xl" py={{ base: 8, md: 12 }}>
          <VStack spacing={8} align="center">
            {/* Section Title */}

            <Heading
              as="h1"
              fontSize={{ base: "2xl", md: "4xl" }}
              fontWeight="bold"
              color="#000B8C"
              lineHeight="1.2"
            >
              Core Modules{" "}
              <Text as="span" color="gray.700">
                & Capabilities
              </Text>
            </Heading>
            {/* Features Grid */}
            <Grid
              templateColumns={{
                base: "1fr",
                md: "repeat(2, 1fr)",
                lg: "repeat(3, 1fr)",
              }}
              gap={6}
              w="100%"
              mt={8}
              justifyItems="center"
            >
              {coreModules.map((feature, index) => (
                <GridItem key={index} w="100%" maxW="400px">
                  <FeatureCard
                    icon={feature.icon}
                    title={feature.title}
                    description={feature.description}
                  />
                </GridItem>
              ))}
            </Grid>
          </VStack>
        </Container>

        {/* Use Cases Section */}
        <CommonFeatureCard title="Use Cases" features={useCases} />

        {/* Why Business Automation for InsightDB Section */}
        <Container maxW="7xl" py={{ base: 8, md: 12 }}>
          <VStack spacing={8} align="center">
            <Heading
              as="h1"
              fontSize={{ base: "2xl", md: "4xl" }}
              fontWeight="bold"
              color="#000B8C"
              lineHeight="1.2"
            >
              Why Business Automation{" "}
              <Text as="span" color="gray.700">
                for InsightDB
              </Text>
            </Heading>

            <Box
              display="flex"
              flexWrap="wrap"
              gap={6}
              w="100%"
              mt={8}
              justifyContent="center"
              alignItems="stretch"
            >
              {whyChooseFeatures.map((feature) => (
                <Box
                  key={feature.id}
                  w="100%"
                  maxW="sm"
                  minW={{ base: "100%", sm: "280px", md: "300px" }}
                  flex={{
                    base: "1 1 100%",
                    sm: "1 1 calc(50% - 12px)",
                    lg: "1 1 calc(33.333% - 16px)",
                  }}
                  display="flex"
                >
                  <ServiceCard service={feature} />
                </Box>
              ))}
            </Box>
          </VStack>
        </Container>
      </Box>
      <Container maxW="7xl" py={8}>
        <Box textAlign="center">
          <VStack spacing={4} align="center">
            <HStack spacing={2} align="center">
              <Text fontSize={{ base: "md", md: "lg" }} color="gray.600">
                Watch, Learn, Grow:
              </Text>
              <Box
                as="a"
                href="https://www.youtube.com/@InsightDB"
                target="_blank"
                rel="noopener noreferrer"
                display="inline-flex"
                alignItems="center"
                justifyContent="center"
                w="40px"
                h="40px"
                bg="red.500"
                borderRadius="md"
                color="white"
                _hover={{
                  bg: "red.600",
                  transform: "scale(1.05)",
                }}
                transition="all 0.2s ease"
                cursor="pointer"
              >
                <FaYoutube size="20px" />
              </Box>

              {/* LinkedIn Link */}
              <Box
                as="a"
                href="https://www.linkedin.com/company/insightdb/"
                target="_blank"
                rel="noopener noreferrer"
                display="inline-flex"
                alignItems="center"
                justifyContent="center"
                w="40px"
                h="40px"
                bg="blue.700"
                borderRadius="md"
                color="white"
                _hover={{
                  bg: "blue.800",
                  transform: "scale(1.05)",
                }}
                transition="all 0.2s ease"
                cursor="pointer"
              >
                <FaLinkedin size="20px" />
              </Box>
            </HStack>
            <Text
              fontSize={{ base: "md", md: "lg" }}
              color="gray.600"
              lineHeight="1.6"
            >
              For more details, please visit{" "}
              <Text
                as="a"
                href="https://insightdb.ai/"
                target="_blank"
                rel="noopener noreferrer"
                color="#000B8C"
                textDecoration="underline"
                fontWeight="medium"
                _hover={{
                  color: "blue.700",
                  textDecoration: "none",
                }}
              >
                www.insightdb.ai
              </Text>
            </Text>

            <Button
              onClick={onOpen}
              rightIcon={<ChevronRightIcon boxSize={5} />}
              bgGradient="linear(to-r, #5B00FF, #FF00CC)"
              color="white"
              size="md"
              fontWeight="semibold"
              borderRadius="full"
              px={8}
              py={6}
              border="2px solid rgba(147, 197, 253, 0.9)"
              boxShadow="0 0 0 4px rgba(147, 197, 253, 0.55), 0 8px 18px rgba(0,0,0,0.15)"
              _hover={{
                filter: "brightness(1.06)",
                transform: "translateY(-2px)",
                boxShadow: "0 0 0 5px rgba(147, 197, 253, 0.65), 0 12px 22px rgba(0,0,0,0.2)",
              }}
              _active={{
                transform: "translateY(0)",
                boxShadow: "0 0 0 4px rgba(147, 197, 253, 0.55), 0 6px 14px rgba(0,0,0,0.12)",
              }}
              transition="all 0.2s ease"
              mt={4}
            >
              Take a Tour
            </Button>
            </VStack>
            </Box>
            </Container>

      {/* Redesigned Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered motionPreset="scale">
        <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(6px)" />
        <ModalContent
          mx={4}
          borderRadius="2xl"
          boxShadow="0 16px 40px rgba(0,0,0,0.18)"
          border="1px solid"
          borderColor="gray.200"
          bg="white"
          p={{ base: 2, md: 0 }}
        >
          <ModalCloseButton
            top={3}
            right={3}
            boxSize={9}
            bg="white"
            border="1px solid"
            borderColor="gray.200"
            borderRadius="full"
            boxShadow="sm"
            _hover={{ bg: "gray.50", transform: "scale(1.06)" }}
            _active={{ bg: "gray.100" }}
          />
          <ModalHeader
            fontSize={{ base: "xl", md: "2xl" }}
            fontWeight="extrabold"
            color="black"
            textAlign="center"
            pt={{ base: 6, md: 8 }}
            pb={2}
          >
            Welcome to the InsightDB Interactive Demo
          </ModalHeader>

          <ModalBody pt={0} pb={{ base: 6, md: 8 }}>
            <VStack spacing={5} align="center" textAlign="center">
              <Text fontSize={{ base: "sm", md: "md" }} color="gray.700" lineHeight="1.6" px={{ base: 2, md: 8 }}>
                You are being redirected to our demo portal. This environment allows you to explore InsightDB's charts,
                dashboards, and interface in action.
              </Text>

              <Box
                w="100%"
                maxW="lg"
                bg="purple.50"
                border="1px solid"
                borderColor="purple.100"
                color="purple.900"
                borderRadius="lg"
                px={{ base: 3, md: 4 }}
                py={{ base: 2.5, md: 3 }}
              >
                <Text fontSize={{ base: "sm", md: "sm" }} fontStyle="italic">
                  Important Note: You may need to log in to access the full visual tour.
                </Text>
              </Box>

              <HStack spacing={4} pt={2}>
                <Button
                  variant="outline"
                  colorScheme="gray"
                  onClick={onClose}
                  borderRadius="md"
                  px={6}
                  bg="gray.100"
                  _hover={{ bg: "gray.200" }}
                >
                  Go Back
                </Button>
                <Button
                  onClick={() => {
                    window.open(demoUrl, "_blank", "noopener,noreferrer");
                    onClose();
                  }}
                  borderRadius="md"
                  px={6}
                  color="white"
                  bgGradient="linear(to-r, #6C4EFF, #8A63FF)"
                  boxShadow="0 8px 18px rgba(108,78,255,0.35)"
                  rightIcon={<ChevronRightIcon />}
                  _hover={{ filter: "brightness(1.05)", transform: "translateY(-1px)" }}
                  _active={{ transform: "translateY(0)" }}
                >
                  Continue to Demo
                </Button>
              </HStack>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
    </>
  );
};

export default InsightDBPage;
